import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Plus, Trash2, PartyPopper } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NEED_TYPES, CONTACT_METHODS, CANTONS } from "@/data/constants";

const Container = ({ children }) => <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">{children}</div>;

export default function RequestWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    services: [], need_type: "", dates: [], recurring: "",
    first_name: "", last_name: "", practice: "", email: "", phone: "",
    city: "", canton: "", contact_method: "E-mail", description: "", consent: false,
  });

  useEffect(() => {
    api.get("/services").then(({ data }) => {
      setServices(data || []);
      const pre = params.get("service");
      if (pre) setForm((f) => ({ ...f, services: [pre] }));
    });
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (title) => setForm((f) => ({ ...f, services: f.services.includes(title) ? f.services.filter((s) => s !== title) : [...f.services, title] }));

  const isTimeBased = useMemo(() => {
    const sel = services.filter((s) => form.services.includes(s.title));
    return sel.some((s) => s.bookable);
  }, [services, form.services]);

  const addDate = () => set("dates", [...form.dates, { date: "", start: "09:00", end: "12:00" }]);
  const updateDate = (i, k, v) => set("dates", form.dates.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
  const removeDate = (i) => set("dates", form.dates.filter((_, idx) => idx !== i));

  const canNext = () => {
    if (step === 1) return form.services.length > 0;
    if (step === 2) return !!form.need_type;
    if (step === 4) return form.first_name && form.last_name && form.email && form.consent;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, dates: form.dates.filter((d) => d.date) };
      const { data } = await api.post("/leads", payload);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <Container>
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12 text-center" data-testid="wizard-success">
          <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><PartyPopper className="h-8 w-8 text-emerald-600" /></div>
          <h1 className="mt-6 font-heading font-bold text-3xl text-slate-900">Merci !</h1>
          <p className="mt-3 text-slate-600 text-lg">Votre demande a bien été transmise à notre équipe.</p>
          <p className="mt-4 text-slate-500 max-w-lg mx-auto">En créant votre espace, vous pourrez suivre votre demande, consulter les disponibilités, gérer votre planning et communiquer directement avec notre équipe.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-primary" data-testid="wizard-create-account"
              onClick={() => navigate("/inscription", { state: { lead: done } })}>Créer mon espace client <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/")}>Retour à l'accueil</Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mb-8">
        <Link to="/" className="text-sm text-slate-500 hover:text-primary">← Retour</Link>
        <h1 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-slate-900">Faire une demande</h1>
        <p className="mt-2 text-slate-600">Sans engagement — vous pourrez créer votre espace ensuite.</p>
        <div className="mt-6 flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= s ? "bg-primary" : "bg-slate-200"}`} />
          ))}
        </div>
        <p className="mt-2 text-xs font-medium text-slate-400 uppercase tracking-wider">Étape {step} sur 4</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
        {step === 1 && (
          <div data-testid="wizard-step-1">
            <h2 className="font-heading font-semibold text-xl text-slate-900">De quoi avez-vous besoin ?</h2>
            <p className="text-sm text-slate-500 mt-1">Sélectionnez un ou plusieurs services.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map((s) => {
                const active = form.services.includes(s.title);
                return (
                  <button key={s.service_id} type="button" onClick={() => toggleService(s.title)} data-testid={`wizard-service-${s.service_id}`}
                    className={`text-left rounded-lg border p-4 transition-all ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-slate-900 text-sm">{s.title}</span>
                      <span className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-primary text-white" : "border border-slate-300"}`}>{active && <Check className="h-3.5 w-3.5" />}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-2">
            <h2 className="font-heading font-semibold text-xl text-slate-900">Votre besoin concerne :</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {NEED_TYPES.map((n) => (
                <button key={n} type="button" onClick={() => set("need_type", n)}
                  className={`text-left rounded-lg border p-4 text-sm font-medium transition-all ${form.need_type === n ? "border-primary bg-primary/5 ring-1 ring-primary text-primary" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-3">
            <h2 className="font-heading font-semibold text-xl text-slate-900">Dates souhaitées</h2>
            {isTimeBased ? (
              <>
                <p className="text-sm text-slate-500 mt-1">Indiquez vos créneaux souhaités (facultatif).</p>
                <div className="mt-5 space-y-3">
                  {form.dates.map((d, i) => (
                    <div key={i} className="flex flex-wrap items-end gap-3 p-3 rounded-lg border border-slate-200">
                      <div><Label className="text-xs">Date</Label><Input type="date" value={d.date} onChange={(e) => updateDate(i, "date", e.target.value)} className="mt-1" /></div>
                      <div><Label className="text-xs">Début</Label><Input type="time" value={d.start} onChange={(e) => updateDate(i, "start", e.target.value)} className="mt-1" /></div>
                      <div><Label className="text-xs">Fin</Label><Input type="time" value={d.end} onChange={(e) => updateDate(i, "end", e.target.value)} className="mt-1" /></div>
                      <Button variant="ghost" size="icon" onClick={() => removeDate(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addDate} data-testid="wizard-add-date"><Plus className="h-4 w-4 mr-1" />Ajouter une date</Button>
                </div>
                <div className="mt-5"><Label className="text-xs">Besoin récurrent (facultatif)</Label>
                  <Input value={form.recurring} onChange={(e) => set("recurring", e.target.value)} placeholder="Ex : chaque lundi après-midi" className="mt-1" /></div>
              </>
            ) : (
              <p className="text-sm text-slate-500 mt-2">Votre demande relève d'un accompagnement / projet. Aucune date de créneau n'est nécessaire — nous conviendrons d'un premier échange.</p>
            )}
            <p className="mt-6 text-xs text-slate-500 bg-slate-50 rounded-lg p-4 border border-slate-100">Ces informations nous permettent de préparer votre demande. Les disponibilités définitives seront confirmées par notre équipe.</p>
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-4">
            <h2 className="font-heading font-semibold text-xl text-slate-900">Vos coordonnées</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Prénom *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1" data-testid="wizard-firstname" /></div>
              <div><Label>Nom *</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1" data-testid="wizard-lastname" /></div>
              <div className="sm:col-span-2"><Label>Cabinet / clinique</Label><Input value={form.practice} onChange={(e) => set("practice", e.target.value)} className="mt-1" /></div>
              <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="wizard-email" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
              <div><Label>Ville</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1" /></div>
              <div><Label>Canton</Label>
                <Select value={form.canton} onValueChange={(v) => set("canton", v)}><SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{CANTONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div className="sm:col-span-2"><Label>Méthode de contact préférée</Label>
                <Select value={form.contact_method} onValueChange={(v) => set("contact_method", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTACT_METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div className="sm:col-span-2"><Label>Description du besoin</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1" /></div>
            </div>
            <p className="mt-4 text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg p-3">Merci de ne pas transmettre de données médicales sensibles concernant vos patients dans cette rubrique.</p>
            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="wizard-consent" />
              <span className="text-sm text-slate-600">J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la politique de confidentialité.</span>
            </label>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}><ArrowLeft className="h-4 w-4 mr-1" />Précédent</Button>
          {step < 4 ? (
            <Button className="bg-primary" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="wizard-next">Continuer <ArrowRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button className="bg-primary" disabled={!canNext() || submitting} onClick={submit} data-testid="wizard-submit">{submitting ? "Envoi..." : "Envoyer ma demande"}</Button>
          )}
        </div>
      </div>
    </Container>
  );
}
